// Firebase Cloud Messaging (FCM) Service for Frontend
// Handles FCM token registration and notification permissions

import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyAoURD-yT18PyBWUIn3TAAFUvn5LDfAJ4w",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "syllera-b7ed3.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "syllera-b7ed3",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "syllera-b7ed3.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "324414726118",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:324414726118:web:5d9ef36a3b95e814b70027",
};

// VAPID key for Web Push
const VAPID_KEY = "BIMob2AmkhZm42L-jHN838vyQY__KkKhTiq9oG3KMrmgX2jmarU5Zi0Hncy_6othzTeUM8ThxAQJBoV50wiUXBU";

// Initialize Firebase
const app = initializeApp(firebaseConfig);

let messaging = null;
let fcmToken = null;

/**
 * Initialize FCM messaging
 * @returns {Promise<Messaging | null>} Firebase messaging instance
 */
export async function initializeFCM() {
  try {
    // Check if browser supports FCM
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('⚠️  Browser does not support notifications');
      return null;
    }

    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️  Service Worker not supported');
      return null;
    }

    // Initialize messaging
    messaging = getMessaging(app);
    
    // Request notification permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('✅ Notification permission granted');
      
      // Get FCM token
      fcmToken = await getFCMToken();
      return messaging;
    } else {
      console.warn('⚠️  Notification permission denied');
      return null;
    }
  } catch (error) {
    console.error('❌ Error initializing FCM:', error);
    return null;
  }
}

/**
 * Get FCM token for current device
 * @returns {Promise<string | null>} FCM token or null
 */
export async function getFCMToken() {
  try {
    if (!messaging) {
      messaging = getMessaging(app);
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY
    });

    if (token) {
      console.log('✅ FCM Token obtained:', token);
      fcmToken = token;
      return token;
    } else {
      console.warn('⚠️  No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting FCM token:', error);
    return null;
  }
}

/**
 * Register FCM token with backend
 * @param {string} authToken - JWT authentication token
 * @returns {Promise<boolean>} Success status
 */
export async function registerFCMToken(authToken) {
  try {
    const token = await getFCMToken();
    
    if (!token) {
      console.warn('⚠️  No FCM token to register');
      return false;
    }

    const platform = getPlatform();
    
    const response = await fetch('/api/notifications/register-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        fcmToken: token,
        platform: platform
      })
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ FCM token registered:', data);
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Error registering FCM token:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error registering FCM token:', error);
    return false;
  }
}

/**
 * Get current platform
 * @returns {string} Platform name
 */
function getPlatform() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  
  return 'web';
}

/**
 * Listen for foreground messages
 * @param {Function} callback - Callback function to handle messages
 */
export function onForegroundMessage(callback) {
  if (!messaging) {
    console.warn('⚠️  FCM not initialized');
    return;
  }

  onMessage(messaging, (payload) => {
    console.log('📬 Foreground message received:', payload);
    if (callback) {
      callback(payload);
    }
  });
}

/**
 * Get stored FCM token
 * @returns {string | null} Stored FCM token
 */
export function getStoredFCMToken() {
  return fcmToken;
}

// Auto-initialize on import (optional)
// Uncomment if you want automatic initialization
// initializeFCM();

export default {
  initializeFCM,
  getFCMToken,
  registerFCMToken,
  onForegroundMessage,
  getStoredFCMToken
};
